import { useEffect } from 'react';
import { COURSE_HREF } from '../../lib/landing';
import { bloborCopy, bloborShowcaseLink, type ShowcaseStory } from '../../lib/strings/blobor';
import { useLocale } from '../../state/useLocale';
import { Brand, LinkButton } from '../../ui';

/**
 * `/blobor`: Blobor, our own game, built entirely by AI agents, as the
 * platform's showcase (owner, 2026-10-31). It replaced the paid task board:
 * nobody is paid for work on the game any more; the game is shown instead,
 * with its history's numbers and three changes measured before and after.
 *
 * Every figure is the repository's own (lib/strings/blobor.ts says where each
 * comes from). Drawn in the frame the other standalone pages use (.projects,
 * .tasks__nav), with its own pieces in styles/blobor.css.
 */
export function BloborShowcase() {
  const { locale } = useLocale();
  const s = bloborCopy[locale];

  useEffect(() => {
    document.title = s.docTitle;
  }, [s]);

  return (
    <main className="projects blobor-showcase">
      <nav className="tasks__nav" aria-label={s.kicker}>
        <Brand href="/" />
        <div>
          <a href={'/' + COURSE_HREF}>{s.navCourse}</a>
          <a href="/agent">{s.navAgent}</a>
        </div>
      </nav>

      <header className="projects__head">
        <p className="projects__kicker">{s.kicker}</p>
        <h1 className="projects__title">{s.title}</h1>
        <p className="projects__lede">{s.lede}</p>
        <p className="blobor-showcase__actions">
          <LinkButton variant="solid" size="lg" href={bloborShowcaseLink()} target="_blank" rel="noreferrer noopener">{s.play}</LinkButton>
          <LinkButton size="lg" href="/">{s.course}</LinkButton>
        </p>
      </header>

      <figure className="blobor-showcase__shot">
        <img src="/shots/blobor-haven.webp" alt={s.shotAlt} width={1280} height={800} loading="lazy" decoding="async" />
      </figure>

      <section className="blobor-showcase__numbers" aria-labelledby="blobor-numbers">
        <h2 className="label" id="blobor-numbers">{s.numbersTitle}</h2>
        <ul>
          {s.numbers.map((n) => (
            <li key={n.label}>
              <strong>{n.value}</strong>
              <span>{n.label}</span>
            </li>
          ))}
        </ul>
        <p className="note">{s.numbersNote}</p>
      </section>

      <section className="blobor-showcase__stories" aria-labelledby="blobor-stories">
        <h2 className="blobor-showcase__h2" id="blobor-stories">{s.storiesTitle}</h2>
        <p className="blobor-showcase__lede">{s.storiesLede}</p>
        {s.stories.map((story) => <Story key={story.id} story={story} />)}
      </section>

      <section className="block blobor-showcase__next" aria-labelledby="blobor-next">
        <h2 className="blobor-showcase__h2" id="blobor-next">{s.nextTitle}</h2>
        <p>{s.nextBody}</p>
        <p><LinkButton variant="solid" href="/desk">{s.nextCta}</LinkButton></p>
      </section>
    </main>
  );
}

function Story({ story }: { story: ShowcaseStory }) {
  const { locale } = useLocale();
  const s = bloborCopy[locale];
  return (
    <article className="block blobor-showcase__story" id={story.id} aria-labelledby={`story-${story.id}`}>
      <p className="note blobor-showcase__date">{story.date}</p>
      <h3 className="blobor-showcase__h3" id={`story-${story.id}`}>{story.title}</h3>
      <dl className="blobor-showcase__parts">
        <dt>{s.problem}</dt>
        <dd>{story.problem}</dd>
        <dt>{s.change}</dt>
        <dd>{story.change}</dd>
      </dl>
      <table className="blobor-showcase__table">
        <thead>
          <tr>
            <th scope="col"><span className="sr-only">{story.title}</span></th>
            <th scope="col">{s.before}</th>
            <th scope="col">{s.after}</th>
          </tr>
        </thead>
        <tbody>
          {story.rows.map((row) => (
            <tr key={row.label}>
              <th scope="row">{row.label}</th>
              <td>{row.before}</td>
              <td className="is-after">{row.after}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="note">{story.measured}</p>
      <p className="blobor-showcase__lesson"><strong>{s.lesson}</strong> {story.lesson}</p>
    </article>
  );
}

export default BloborShowcase;
